import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpContext } from '@angular/common/http';
import { backendEndPointToken, terminologyCanonicalUrlBaseToken } from '../app.config';
import { Observable, tap, switchMap, forkJoin, map, of, catchError } from 'rxjs';
import { Annotation, Appointment, Slot, ServiceRequest, Bundle } from 'fhir/r4';
import { SuccessToastService } from '../shared/success-toast.service';
import { StateService } from '../shared/state.service';
import { LoadingUIEnabled, SkipHospitalSegregation } from '../loading.interceptor';
import { AuthService } from '../shared/auth/auth.service';
import { authBackendEndPointToken } from '../app.config';
import { EncounterServiceService } from '../patient-wrapper/encounter-service.service';
import { SystemRoles } from '../shared/system-roles';

@Injectable({
    providedIn: 'root'
})
export class AppointmentService {
    backendUrl = inject(backendEndPointToken);
    backendApiUrl = inject(authBackendEndPointToken);
    terminologyBaseUrl = inject(terminologyCanonicalUrlBaseToken);
    tenantTagSystem = `${this.terminologyBaseUrl}/CodeSystem/organizationTenant`;

    http = inject(HttpClient);
    successToast = inject(SuccessToastService);
    auth = inject(AuthService);
    stateService = inject(StateService);
    encounterService = inject(EncounterServiceService);


    /*
      createAppointment(slot: Slot, patientId: string, practitionerRef: string, otherParticipants: any[] = []): Observable<Appointment> {
      // Get the organization ID from the current user
      const orgId = this.auth.user.getValue()?.organizationId || '';
  
      // Normalize orgId to handle both "xxx" and "Organization/xxx" formats
      const normalizedOrgId = orgId.startsWith('Organization/') ? orgId : `Organization/${orgId}`;
  
      const appointment: any = {
          resourceType: 'Appointment',
          status: 'proposed',
          description: 'Consultation Request',
          start: slot.start,
          end: slot.end,
          slot: [{ reference: `Slot/${slot.id}` }],
          participant: [
              {
                  actor: { reference: `Patient/${patientId}` },
                  status: 'needs-action'
              },
              {
                  actor: { reference: practitionerRef },
                  status: 'needs-action'
              },
              ...otherParticipants
          ],
          created: new Date().toISOString(),
  
          // ✅ Add organization extension (optional but included if orgId exists)
          ...(orgId && {
              extension: [
                  {
                      url: "http://your-org/fhir/StructureDefinition/actor-organization",
                      valueReference: { reference: normalizedOrgId }
                  }
              ]
          })
      };
  
      return this.http.post<Appointment>(`${this.backendUrl}/Appointment`, appointment, {
          headers: new HttpHeaders({ 'Content-Type': 'application/fhir+json' })
      }).pipe(
          tap(() => this.successToast.success('Appointment created successfully.'))
      );
  }
  */
    createAppointment(
        slot: Slot | null,
        patientId: string,
        practitionerRef?: string | null,
        enableZoom: boolean = true,
        otherParticipants: any[] = [],
        isTelemedicineByPatient: boolean = false,
        appointmentTypeCoding?: any,
        serviceTypeCoding?: any,
        basedOnReferences: string[] = [],
        requestingPractitionerRef?: string | null,
        practitionerOnlyParticipants: boolean = false,
        requestedPeriodStart?: string,
        requestedPeriodEnd?: string,
        minutesDuration?: number,
        patientInstruction?: string,
        isVirtual: boolean = true,
        specialtyCode?: string,
        specialtyDisplay?: string
    ): Observable<any> {
        const request = {
            patientId,
            practitionerRef: practitionerRef || undefined,
            slotId: slot?.id,
            enableZoom,
            zoomPlatform: 'zoom',
            failOnZoomError: false,
            isTelemedicineByPatient,
            practitionerOnlyParticipants,
            terminologyCanonicalBase: this.terminologyBaseUrl,
            otherParticipants: otherParticipants,
            requestedPeriodStart,
            requestedPeriodEnd,
            minutesDuration,
            patientInstruction,
            isVirtual,
            SpecialtyCode: specialtyCode,
            SpecialtyDisplay: specialtyDisplay
        };

        console.log('📤 createAppointment received:', {
            slot,
            patientId,
            practitionerRef,
            otherParticipants,
            isTelemedicineByPatient,
            practitionerOnlyParticipants
        });
        console.log('📦 Request payload being sent to backend:', request);
        return this.http.post<any>(
            `${this.backendApiUrl}/api/appointment/create`,
            request
        ).pipe(
            switchMap((response) => {
                // Retrieve created appointment from response. Assuming it's in response.appointment or response itself.
                const createdApptId = response.appointment?.id || response.appointmentId || response.id;

                if (createdApptId) {
                    // Fetch the bare-bones appointment from FHIR backend, augment it and PUT it back.
                    return this.augmentAndSyncAppointment(
                        createdApptId,
                        patientId,
                        practitionerRef || '',
                        isTelemedicineByPatient,
                        appointmentTypeCoding,
                        serviceTypeCoding,
                        basedOnReferences,
                        requestingPractitionerRef,
                        practitionerOnlyParticipants
                    ).pipe(
                        map(() => {
                            this.successToast.success('Appointment created and augmented successfully!');
                            if (response.zoomMeetingUrl) {
                                console.log('Zoom URL:', response.zoomMeetingUrl);
                            }
                            return response;
                        }),
                        catchError((err) => {
                            console.error('Failed to augment appointment on FHIR backend, but creation succeeded', err);
                            // We still return the positive response as the core creation worked, just augmentation failed.
                            this.successToast.success('Appointment created successfully (augmentation pending)!');
                            return of(response);
                        })
                    );
                }

                this.successToast.success('Appointment created successfully!');
                return of(response);
            }),
            catchError((error) => {
                console.error('Error:', error);
                throw error;
            })
        );
    }

    /// <summary>
    /// Update an existing appointment with optional Zoom meeting update
    /// </summary>
    updateAppointment(
        appointmentId: string,
        slot?: Slot,
        status?: string,
        description?: string,
        topic?: string,
        changeDetails?: string,
        updateZoomDetails: boolean = false,
        notifyParticipants: boolean = true,
        zoomPassword?: string,
        practitionerRef?: string
    ): Observable<any> {
        const request = {
            slotId: slot?.id,           // Optional: new slot if rescheduling
            status: status,             // Optional: new status (booked, fulfilled, etc.)
            description: description,   // Optional: new description
            topic: topic,               // Optional: new meeting topic
            changeDetails: changeDetails, // Optional: reason for change
            updateZoomDetails: updateZoomDetails, // Update Zoom meeting if changed
            zoomPassword: zoomPassword, // Optional: new Zoom password
            notifyParticipants: notifyParticipants, // Notify participants of change
            practitionerRef: practitionerRef // Optional: new practitioner
        };

        return this.http.put<any>(
            `${this.backendApiUrl}/api/appointment/${appointmentId}`,
            request
        ).pipe(
            tap((response) => {
                this.successToast.success('Appointment updated successfully!');
                if (response.zoomMeetingUrl) {
                    console.log('Updated Zoom URL:', response.zoomMeetingUrl);
                }
            }),
            catchError((error) => {
                // Handle 409 Conflict (time slot conflict)
                if (error.status === 409) {
                    console.error('Time slot conflict:', error.error);
                    //this.successToast.error('Cannot reschedule: Patient already has an appointment at that time');
                } else {
                    console.error('Error:', error);
                    //this.successToast.error('Failed to update appointment');
                }
                throw error;
            })
        );
    }

    /**
       * Cancel an appointment
       */
    cancelAppointment(
        appointmentId: string,
        cancelReason?: string,
        notifyParticipants?: boolean,
        organizationId?: string
    ): Observable<any> {
        const request = {
            cancelReason: cancelReason || undefined,
            notifyParticipants: notifyParticipants ?? true,
            organizationId: organizationId || undefined
        };

        return this.http.post(`${this.backendApiUrl}/api/appointment/${appointmentId}/cancel`, request);
    }

    /**
     * ✅ NEW METHOD: Practitioner responds to appointment (Accept or Decline)
     * 
     * @param appointmentId - The appointment ID to respond to
     * @param accept - true to accept, false to decline
     * @param declineReason - Required if accepting. Reason for declining.
     * @param confirmOnBehalfOfPatientId - Optional. Patient ID to confirm on behalf of (no Patient/ prefix)
     * @returns Observable with response
     */
    respondToAppointment(
        appointmentId: string,
        accept: { status: Appointment['participant']['0']['status'] },
        declineReason?: string,
        confirmOnBehalfOfPatientId?: string
    ): Observable<any> {
        const request = {
            ...accept,
            declineReason: declineReason || undefined,
            confirmOnBehalfOfPatientId: confirmOnBehalfOfPatientId || undefined
        };

        return this.http.post(
            `${this.backendApiUrl}/api/appointment/${appointmentId}/respond`,
            request
        );
    }

    /**
     * Get appointment details
     */
    getAppointment(appointmentId: string): Observable<any> {
        return this.http.get(`${this.backendApiUrl}/api/appointment/${appointmentId}`);
    }

    getAppointmentForMeeting(appointmentId: string): Observable<any> {
        const normalizeResponse = (response: any) => {
            const appointment = response?.appointment ?? response?.resource ?? response;
            if (!appointment || typeof appointment !== 'object') return appointment;

            return {
                ...appointment,
                zoomMeetingUrl:
                    response?.zoomMeetingUrl ??
                    response?.zoomUrl ??
                    response?.joinUrl ??
                    response?.join_url ??
                    appointment?.zoomMeetingUrl,
                zoomMeetingId:
                    response?.zoomMeetingId ??
                    response?.meetingId ??
                    response?.meeting_id ??
                    appointment?.zoomMeetingId,
                zoomPassword:
                    response?.zoomPassword ??
                    response?.password ??
                    response?.passcode ??
                    appointment?.zoomPassword
            };
        };

        return this.getAppointment(appointmentId).pipe(
            map(normalizeResponse),
            catchError(() =>
                this.http.get<Appointment>(`${this.backendUrl}/Appointment/${encodeURIComponent(appointmentId)}`)
                    .pipe(map(normalizeResponse))
            )
        );
    }

    /**
     * Get a Zoom SDK signature for the appointment meeting
     */
    getZoomSignature(appointmentId: string, role: number = 0): Observable<any> {
        return this.http.get<any>(`${this.backendApiUrl}/api/appointment/${appointmentId}/zoom-signature?role=${role}`);
    }

    /**
     * Get a Zoom ZAK token for starting an appointment meeting as host
     */
    getZoomZak(appointmentId: string): Observable<any> {
        return this.http.get<any>(`${this.backendApiUrl}/api/appointment/${appointmentId}/zoom-zak`);
    }

    // Stub for future implementation
    generateMeetingLink(appointmentId: string, platform: 'zoom' | 'google-meet'): Observable<string> {
        // This would call a backend service that interfaces with Zoom/Google APIs
        // For now, return a mock link
        return new Observable(obs => {
            setTimeout(() => {
                obs.next(`https://${platform}.com/meeting/${appointmentId}-${Math.random().toString(36).substring(7)}`);
                obs.complete();
            }, 1000);
        });
    }

    createServiceRequest(practitionerRef: string, patientId: string, encounterId: string, requesterRef: string, noteText?: string): Observable<ServiceRequest> {
        const serviceRequest: any = {
            resourceType: 'ServiceRequest',
            status: 'active',
            intent: 'order',
            category: [{
                coding: [{
                    system: this.backendUrl,
                    code: 'consultation',
                    display: 'Consultation'
                }]
            }],
            code: {
                coding: [{
                    system: this.backendUrl,
                    code: 'consultation',
                    display: 'Consultation'
                }]
            },
            subject: { reference: `Patient/${patientId}` },
            encounter: { reference: `Encounter/${encounterId}` },
            authoredOn: new Date().toISOString(),
            performerType: {
                coding: [{
                    system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType',
                    code: 'CON',
                    display: 'Consultant'
                }],
                text: 'Consultant'
            },
            performer: [{ reference: practitionerRef }],
            requester: { reference: requesterRef },
            note: this.buildConsultationNotes(noteText)
        };

        return this.http.post<ServiceRequest>(`${this.backendUrl}/ServiceRequest`, serviceRequest, {
            headers: new HttpHeaders({ 'Content-Type': 'application/fhir+json' })
        }).pipe(
            tap((createdSr) => {
                this.encounterService.notifyServiceRequestCreated(createdSr, {
                    category: [{ text: 'consultation' }],
                    code: serviceRequest.code,
                    priority: serviceRequest.priority,
                    intent: serviceRequest.intent
                }, {
                    legacyTargetRolesOverride: [SystemRoles.Consultant],
                    messagingRecipientRolesOverride: [SystemRoles.Consultant]
                });
            }),
            tap((createdSr) => {
                this.successToast.success('Consultation request sent successfully.');
                const currentPatientId = this.stateService.currentPatientId$.getValue();
                if (currentPatientId) {
                    this.stateService.persistPatientResource(createdSr, 'saved');
                } else {
                    this.stateService.persistOrgWideResource(createdSr, 'saved');
                }
            })
        );
    }

    createConsultationWithAppointment(
        slot: Slot | null | undefined,
        patientId: string,
        practitionerRef: string | null,
        encounterId: string,
        requesterRef: string,
        otherParticipants: any[] = [],
        bookingType: string = 'Consultation Request',
        isTelemedicineByPatient: boolean = false,
        appointmentTypeCoding?: any,
        serviceTypeCoding?: any,
        requestingPractitionerRef?: string | null,
        practitionerOnlyAppointment: boolean = false,
        noteText?: string,
        extraParams?: {
            requestedPeriodStart?: string;
            requestedPeriodEnd?: string;
            minutesDuration?: number;
            patientInstruction?: string;
            isVirtual?: boolean;
            specialtyCode?: string;
            specialtyDisplay?: string;
        }
    ): Observable<any> {
        const serviceRequest: any = {
            resourceType: 'ServiceRequest',
            status: 'active',
            intent: 'order',
            category: [{
                coding: [{
                    system: this.backendUrl,
                    code: 'consultation',
                    display: 'Consultation'
                }]
            }],
            code: {
                coding: [{
                    system: this.backendUrl,
                    code: 'consultation',
                    display: 'Consultation'
                }]
            },
            subject: { reference: `Patient/${patientId}` },
            encounter: { reference: `Encounter/${encounterId}` },
            authoredOn: new Date().toISOString(),
            performerType: {
                coding: [{
                    system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType',
                    code: 'CON',
                    display: 'Consultant'
                }],
                text: 'Consultant'
            },
            performer: [{ reference: practitionerRef }],
            requester: { reference: requesterRef },
            note: this.buildConsultationNotes(noteText)
        };

        return this.http.post<ServiceRequest>(`${this.backendUrl}/ServiceRequest`, serviceRequest, {
            headers: new HttpHeaders({ 'Content-Type': 'application/fhir+json' })
        }).pipe(
            switchMap((createdSr) => {
                this.encounterService.notifyServiceRequestCreated(createdSr, {
                    category: [{ text: 'consultation' }],
                    code: serviceRequest.code,
                    priority: serviceRequest.priority,
                    intent: serviceRequest.intent
                }, {
                    legacyTargetRolesOverride: [SystemRoles.Consultant],
                    messagingRecipientRolesOverride: [SystemRoles.Consultant]
                });

                const currentPatientId = this.stateService.currentPatientId$.getValue();
                if (currentPatientId) {
                    this.stateService.persistPatientResource(createdSr, 'saved');
                } else {
                    this.stateService.persistOrgWideResource(createdSr, 'saved');
                }

                // 2. Create Appointment via Custom Backend
                return this.createAppointment(
                    slot || null,
                    patientId,
                    practitionerRef,
                    true,
                    otherParticipants,
                    isTelemedicineByPatient,
                    appointmentTypeCoding,
                    serviceTypeCoding,
                    createdSr.id ? [`ServiceRequest/${createdSr.id}`] : [],
                    requestingPractitionerRef,
                    practitionerOnlyAppointment,
                    extraParams?.requestedPeriodStart,
                    extraParams?.requestedPeriodEnd,
                    extraParams?.minutesDuration,
                    extraParams?.patientInstruction,
                    extraParams?.isVirtual ?? true,
                    extraParams?.specialtyCode,
                    extraParams?.specialtyDisplay
                ).pipe(
                    switchMap((apptResponse: any) => {
                        const apptId = apptResponse?.appointment?.id || apptResponse?.appointmentId || apptResponse?.id;
                        if (apptId) {
                            return this.updateAppointment(apptId, undefined, undefined, bookingType).pipe(
                                map(() => apptResponse),
                                catchError(() => of(apptResponse)) // proceed even if update fails
                            );
                        }
                        return of(apptResponse);
                    })
                );
            }),
            tap(() => {
                this.successToast.success('Consultation and Appointment created successfully.');
            })
        );
    }

    private buildConsultationNotes(noteText?: string): Annotation[] | undefined {
        const normalizedNote = (noteText ?? '').trim();
        if (!normalizedNote) {
            return undefined;
        }

        return [{ text: normalizedNote }];
    }

    private resolveParticipantContacts$(participants: any[]): Observable<{ participantsWithMetadata: any[], extensions: any[] }> {
        const refsToFetch = participants
            .map(p => p.actor?.reference)
            .filter((ref): ref is string => !!ref && (ref.startsWith('Patient/') || ref.startsWith('Practitioner/')));

        if (refsToFetch.length === 0) {
            return of({ participantsWithMetadata: participants, extensions: [] });
        }

        const requests = refsToFetch.map(ref => {
            return this.http.get<any>(`${this.backendUrl}/${ref}`, {
                context: new HttpContext().set(LoadingUIEnabled, false)
            }).pipe(
                map(resource => ({ ref, resource })),
                catchError(() => of({ ref, resource: null }))
            );
        });

        return forkJoin(requests).pipe(
            map((results: any[]) => {
                const extensions: any[] = [];
                const resMap = new Map<string, any>(results.map(r => [r.ref, r.resource]));

                results.forEach(({ ref, resource }: { ref: string, resource: any }) => {
                    if (!resource) return;
                    const type = ref.startsWith('Patient/') ? 'patient' : 'practitioner';
                    const id = resource.id;

                    const telecom = (resource.telecom ?? []) as Array<{ system?: string; value?: string }>;
                    const email = telecom.find(t => t.system === 'email')?.value;
                    const phone = telecom.find(t => t.system === 'phone')?.value;

                    if (email) {
                        extensions.push({
                            url: "http://hospital.org/fhir/StructureDefinition/appointment-emails",
                            extension: [
                                { url: "participantType", valueString: type },
                                { url: "value", valueString: email },
                                { url: "id", valueString: id }
                            ]
                        });
                    }

                    if (phone) {
                        extensions.push({
                            url: "http://hospital.org/fhir/StructureDefinition/appointment-phones",
                            extension: [
                                { url: "participantType", valueString: type },
                                { url: "value", valueString: phone },
                                { url: "id", valueString: id }
                            ]
                        });
                    }

                    if (type === 'patient') {
                        extensions.push({
                            url: "http://hospital.org/fhir/StructureDefinition/appointment-patient-id",
                            valueReference: {
                                reference: ref,
                                display: resource.name ? (resource.name[0]?.text || resource.name[0]?.family) : 'Patient'
                            }
                        });
                    }
                });

                return { participantsWithMetadata: participants, extensions };
            })
        );
    }

    private augmentAndSyncAppointment(
        appointmentId: string,
        patientId: string,
        practitionerRef: string,
        isTelemedicineByPatient: boolean,
        appointmentTypeCoding?: any,
        serviceTypeCoding?: any,
        basedOnReferences: string[] = [],
        requestingPractitionerRef?: string | null,
        practitionerOnlyParticipants: boolean = false
    ): Observable<any> {
        return this.http.get<Appointment>(`${this.backendUrl}/Appointment/${appointmentId}`).pipe(
            switchMap((bareAppt: Appointment) => {
                // 1. Resolve participants resources to find their organizations
                const patientReq = this.http.get<any>(`${this.backendUrl}/Patient/${patientId}`).pipe(catchError(() => of(null)));
                const practitionerRefPath = practitionerRef.startsWith('Practitioner/') ? practitionerRef : `Practitioner/${practitionerRef}`;
                const practitionerReq = this.http.get<any>(`${this.backendUrl}/${practitionerRefPath}`).pipe(catchError(() => of(null)));

                // 1b. Resolve PractitionerRole as reliable fallback for organization
                const practitionerId = practitionerRefPath.split('/').pop();
                const practitionerRoleReq = this.http.get<Bundle<any>>(`${this.backendUrl}/PractitionerRole?practitioner=${practitionerId}&_count=1`).pipe(
                    catchError(() => of(null))
                );

                return forkJoin({
                    patient: patientReq,
                    practitioner: practitionerReq,
                    practitionerRole: practitionerRoleReq
                }).pipe(
                    switchMap(({ patient, practitioner, practitionerRole }) => {
                        let patientOrgId = patient?.managingOrganization?.reference?.split('/').pop();

                        // Find practitioner organization (using standard logic, PractitionerRole, or extension)
                        let practitionerOrgId = '';
                        const actorOrgExt = practitioner?.extension?.find(
                            (e: any) => e.url === 'http://e-likita.org/fhir/StructureDefinition/actor-organization' || e.url === 'hospitalId'
                        );

                        if (actorOrgExt?.valueReference?.reference) {
                            practitionerOrgId = actorOrgExt.valueReference.reference.split('/').pop();
                        } else if (practitionerRole?.entry?.[0]?.resource?.organization?.reference) {
                            // Standard FHIR R4: Resolve organization via PractitionerRole
                            practitionerOrgId = practitionerRole.entry[0].resource.organization.reference.split('/').pop();
                        } else if (practitioner?.organization?.reference) {
                            practitionerOrgId = practitioner.organization.reference.split('/').pop();
                        } else {
                            // Attempt to get from logged in user if this is the practitioner creating it
                            const currentUser = this.auth.user.getValue();
                            if (currentUser?.role && ['doctor', 'nurse', 'consultant'].includes(currentUser.role)) {
                                practitionerOrgId = currentUser.organizationId || currentUser.hospitalId || '';
                            }
                        }

                        // Determine primary tag and security tag based on logic
                        let primaryTagOrgId = '';
                        let securityTagOrgId = '';

                        if (isTelemedicineByPatient) {
                            primaryTagOrgId = patientOrgId || '';
                            securityTagOrgId = practitionerOrgId || '';
                        } else {
                            // If not explicitly telemedicine by patient, compare organizations
                            if (patientOrgId && practitionerOrgId && patientOrgId !== practitionerOrgId) {
                                // Distinct organizations: Patient's hospital owns it, Practitioner's hospital is shared
                                primaryTagOrgId = patientOrgId;
                                securityTagOrgId = practitionerOrgId;
                            } else {
                                // Same organization or one is missing: whoever we found is primary
                                primaryTagOrgId = patientOrgId || practitionerOrgId || '';
                            }
                        }

                        // Augment the appointment resource
                        const updatedAppt: Appointment = { ...bareAppt };

                        if (practitionerOnlyParticipants) {
                            const normalizedRequesterRef = this.normalizePractitionerReference(requestingPractitionerRef);
                            const consultantRef = this.normalizePractitionerReference(practitionerRefPath);
                            const participants = this.removePatientParticipants(updatedAppt.participant);

                            if (consultantRef) {
                                this.ensureParticipant(participants, consultantRef, 'needs-action', 'CON', 'Consultant');
                            }

                            if (normalizedRequesterRef && normalizedRequesterRef !== consultantRef) {
                                this.ensureParticipant(participants, normalizedRequesterRef, 'accepted', 'PPRF', 'Requesting Clinician');
                            }

                            updatedAppt.participant = participants;
                        }

                        // Add appointment type if provided
                        if (appointmentTypeCoding && !updatedAppt.appointmentType) {
                            updatedAppt.appointmentType = {
                                coding: [appointmentTypeCoding]
                            };
                        }

                        // Add service type if provided
                        if (serviceTypeCoding && !updatedAppt.serviceCategory) {
                            updatedAppt.serviceCategory = [{
                                coding: [serviceTypeCoding]
                            }];
                        }

                        if (basedOnReferences.length) {
                            const existingBasedOn = Array.isArray(updatedAppt.basedOn) ? updatedAppt.basedOn : [];
                            const nextBasedOn = [...existingBasedOn];

                            for (const reference of basedOnReferences) {
                                if (!reference) {
                                    continue;
                                }

                                if (!nextBasedOn.some(item => item?.reference === reference)) {
                                    nextBasedOn.push({ reference });
                                }
                            }

                            updatedAppt.basedOn = nextBasedOn;
                        }

                        // Add Tags for Multi-Tenancy
                        updatedAppt.meta = updatedAppt.meta || {};
                        const tagBucket: any[] = updatedAppt.meta.tag || [];
                        const secBucket: any[] = updatedAppt.meta.security || [];

                        if (primaryTagOrgId) {
                            if (!tagBucket.some(t => t.system === this.tenantTagSystem && t.code === primaryTagOrgId)) {
                                tagBucket.push({ system: this.tenantTagSystem, code: primaryTagOrgId });
                            }
                        }

                        if (securityTagOrgId && securityTagOrgId !== primaryTagOrgId) {
                            if (!secBucket.some(s => s.system === this.tenantTagSystem && s.code === securityTagOrgId)) {
                                secBucket.push({ system: this.tenantTagSystem, code: securityTagOrgId });
                            }
                        }
                        // alert(JSON.stringify(updatedAppt.meta))

                        updatedAppt.meta.tag = tagBucket;
                        if (secBucket.length > 0) {
                            updatedAppt.meta.security = secBucket;
                        } else {
                            delete updatedAppt.meta.security;
                        }
//   alert(JSON.stringify(updatedAppt.meta))
                        // PUT the updated resource back
                        return this.http.put<Appointment>(`${this.backendUrl}/Appointment/${appointmentId}`, updatedAppt);
                    })
                );
            })
        );
    }

    getAppointmentsForServiceRequests(serviceRequestIds: string[]): Observable<Appointment[]> {
        const normalizedIds = Array.from(new Set(
            serviceRequestIds
                .map(id => (id ?? '').toString().trim())
                .filter(Boolean)
        ));

        if (!normalizedIds.length) {
            return of([]);
        }

        const chunkSize = 10;
        const context = new HttpContext()
            .set(LoadingUIEnabled, false)
            .set(SkipHospitalSegregation, true);
        const queries: Observable<Appointment[]>[] = [];

        for (let index = 0; index < normalizedIds.length; index += chunkSize) {
            const chunk = normalizedIds.slice(index, index + chunkSize);
            const params = chunk
                .map(id => `based-on=${encodeURIComponent(`ServiceRequest/${id}`)}`)
                .join('&');
            const url = `${this.backendUrl}/Appointment?${params}&_count=200`;
            queries.push(
                this.http.get<Bundle<Appointment>>(url, { context }).pipe(
                    map(bundle => (bundle.entry ?? [])
                        .map(entry => entry.resource as Appointment)
                        .filter(resource => resource?.resourceType === 'Appointment')),
                    catchError(() => of([]))
                )
            );
        }

        return forkJoin(queries).pipe(
            map(chunks => {
                const seenIds = new Set<string>();
                const appointments: Appointment[] = [];

                for (const chunk of chunks) {
                    for (const appointment of chunk) {
                        if (!appointment?.id) {
                            appointments.push(appointment);
                            continue;
                        }

                        if (seenIds.has(appointment.id)) {
                            continue;
                        }

                        seenIds.add(appointment.id);
                        appointments.push(appointment);
                    }
                }

                return appointments;
            })
        );
    }

    private normalizePractitionerReference(reference: string | null | undefined): string | null {
        if (!reference) {
            return null;
        }

        const trimmed = reference.trim();
        if (!trimmed) {
            return null;
        }

        const noParams = trimmed.split('?')[0];
        return noParams.startsWith('Practitioner/') ? noParams : `Practitioner/${noParams}`;
    }

    private removePatientParticipants(participants: Appointment['participant'] | undefined): Appointment['participant'] {
        if (!Array.isArray(participants)) {
            return [];
        }

        return participants.filter(participant => {
            const actorRef = participant?.actor?.reference ?? '';
            return !!actorRef && !actorRef.startsWith('Patient/');
        });
    }

    private ensureParticipant(
        participants: Appointment['participant'],
        reference: string,
        status: 'accepted' | 'needs-action',
        code: string,
        display: string
    ): void {
        if (!reference) {
            return;
        }

        const exists = participants.some(participant => participant?.actor?.reference === reference);
        if (exists) {
            return;
        }

        participants.push({
            actor: { reference },
            status,
            type: [{
                coding: [{
                    system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType',
                    code,
                    display
                }]
            }]
        });
    }
}
